import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  endImpersonation,
  getImpersonationsForBusiness,
  ImpersonationSession,
} from '../../services/impersonation';
import useUser from '../global/useUser';

const QUERY_KEY = 'impersonation-sessions';
const POLL_INTERVAL_MS = 60_000;
const REFETCH_NEAR_EXPIRY_MS = 5_000;

function useImpersonationBanner() {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const businessId = user?.business?.id;
  const userId = user?.userId;

  // Endpoint is admin-only. During an active impersonation the FE role is
  // munged to SHOP_MANAGER, so also enable when the current user's own ETB on
  // this business is flagged as an impersonation (only admins can have one).
  const isAdmin = user?.role === 'ADMIN';
  const isImpersonatingHere = !!user?.isImpersonation;
  const canQuery = !!businessId && (isAdmin || isImpersonatingHere);

  const { data: sessions } = useQuery({
    queryKey: [QUERY_KEY, businessId],
    queryFn: () =>
      businessId
        ? getImpersonationsForBusiness(businessId)
        : Promise.resolve([]),
    enabled: canQuery,
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: 30_000,
  });

  const mySession: ImpersonationSession | undefined = React.useMemo(
    () => sessions?.find(s => s.employeeId === userId),
    [sessions, userId],
  );

  const expiresAtMs = mySession
    ? new Date(mySession.expiresAt).getTime()
    : null;

  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!expiresAtMs) return undefined;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [expiresAtMs]);

  const msRemaining = expiresAtMs ? Math.max(0, expiresAtMs - now) : 0;

  React.useEffect(() => {
    if (!expiresAtMs) return;
    if (msRemaining <= 0) {
      window.location.href = '/';
      return;
    }
    if (msRemaining <= REFETCH_NEAR_EXPIRY_MS) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, businessId] });
    }
  }, [businessId, expiresAtMs, msRemaining, queryClient]);

  const exitMutation = useMutation({
    mutationFn: () => {
      if (!businessId) throw new Error('No business selected');
      return endImpersonation(businessId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, businessId] });
      window.location.href = '/';
    },
  });

  return {
    isBannerShown: !!mySession && msRemaining > 0,
    business: mySession?.business,
    msRemaining,
    exit: () => exitMutation.mutate(),
    isExiting: exitMutation.isPending,
  };
}

export default useImpersonationBanner;
